import { AnimatePresence, motion } from "framer-motion";
import { Button } from "../components/ui/button.jsx";
import React, { useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";


type ContactSalesModalProps = {
    open: boolean;
    onClose: () => void;
    onCallClick: () => void;
    showKAM: boolean;
    setShowKAM: (t: boolean) => void;
    setOpenSales: (t: boolean) => void;
    client: any;
    baseUrl: string;
};

export default function ContactSalesModal({
    open,
    onClose,
    onCallClick,
    showKAM,
    setShowKAM,
    setOpenSales,
    client,
    baseUrl,

}: ContactSalesModalProps) {

    const getSaleAccountManagers = async ({ signal }) => {
        const response = await axios.get(baseUrl + "/getSaleAgents", {
            params: { client_uid: client?.uid },
            signal: signal
        });

        if (response.data.status === "OK") {
            return response.data.data;
        }

        return [];
    };

    const { data: routedKams = [] } = useQuery({
        queryKey: ["saleAgents", client?.uid],
        queryFn: getSaleAccountManagers,
        refetchInterval: open
                            ? (query) => {
                                const data = query.state.data;
                                return data?.some(k => k.call_status === "available") ? 2000 : 8000;
                            }
                            : false,
        enabled: !!client?.uid,
        staleTime: 3000
    });

    const anyAvailable = routedKams.some(
        kam => kam.call_status === "available"
    );
    return (
        <>
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 10 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 10 }}
                            className="bg-white rounded-2xl w-full max-w-md p-8 relative"
                        >
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
                                aria-label="Close"
                            >
                                ✕
                            </button>

                            <h3 className="text-3xl font-semibold text-slate-900 mb-2">
                                Kontakt salg
                            </h3>
                            <p className="text-slate-600 mb-8">
                                Velg hvordan du ønsker å komme i kontakt med vårt salgsteam.
                            </p>

                            <div className="grid grid-cols-1 gap-4">
                                {anyAvailable && (
                                    <button
                                        onClick={onCallClick}
                                        className="group flex items-center gap-4 rounded-xl border border-slate-200 p-5 hover:border-orange-500 hover:bg-orange-50 transition"
                                    >
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                                            📞
                                        </div>
                                        <div className="text-left">
                                            <div className="text-lg font-medium text-slate-900">
                                                Ring oss
                                            </div>
                                            <div className="text-sm text-slate-600">
                                                Snakk direkte med en rådgiver
                                            </div>
                                        </div>
                                    </button>
                                )}

                                <a
                                    href="mailto:sales@chamcore.com?subject=Henvendelse%20fra%20webområde"
                                    className="group flex items-center gap-4 rounded-xl border border-slate-200 p-5 hover:border-orange-500 hover:bg-orange-50 transition"
                                >
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                                        ✉️
                                    </div>
                                    <div className="text-left">
                                        <div className="text-lg font-medium text-slate-900">
                                            Send e-post
                                        </div>
                                        <div className="text-sm text-slate-600">
                                            Vi svarer deg så raskt som mulig
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Key Account Managers modal */}
            <AnimatePresence>
                {showKAM && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 10 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 10 }}
                            className="bg-white rounded-2xl w-full max-w-lg p-8 relative"
                        >
                            <button
                                onClick={() => setShowKAM(false)}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
                                aria-label="Close"
                            >
                                ✕
                            </button>

                            <button
                                onClick={() => {
                                    setShowKAM(false);
                                    setOpenSales(true);
                                }}
                                className="mb-4 text-sm text-slate-500 hover:text-slate-700"
                            >
                                ← Tilbake
                            </button>

                            <h3 className="text-2xl font-semibold text-slate-900 mb-2">
                                Ring en Key Account Manager
                            </h3>
                            <p className="text-slate-600 mb-6">
                                Velg hvem du ønsker å ringe – du settes direkte over.
                            </p>

                            <div className="space-y-4">
                                {routedKams.map((kam) => (
                                    <div
                                        key={kam.id}
                                        className={`flex items-center justify-between rounded-xl border p-4 transition ${kam.recommended
                                            ? "border-orange-200 bg-orange-50"
                                            : "border-slate-200 bg-white"
                                            }`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <img
                                                src={kam.headshotSrc ?? "/stock/headshot.jpg"}
                                                alt={kam.name}
                                                className="h-12 w-12 rounded-full object-cover border border-slate-200"
                                            />
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <div className="font-medium text-slate-900">{kam.name}</div>
                                                    {kam.recommended && (
                                                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-600">
                                                            Anbefalt
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-slate-600">
                                                    Key Account Manager · {kam.call_status}
                                                </div>
                                            </div>
                                        </div>

                                        {kam.call_status === "available" && (
                                            <a

                                                className={`rounded-lg px-4 py-2 text-white bg-[var(--primary-color)] hover:bg-orange-600`}
                                                href={`tel:${kam.phone_code}${kam.phone}`}
                                            >
                                                Ring
                                            </a>
                                        )}
                                    </div>
                                ))}

                                {routedKams.length === 0 && (
                                    <div className="rounded-xl border border-slate-200 p-4 text-slate-600">
                                        Ingen tilgjengelige selgere for ditt område akkurat nå.
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}